/**
 * AdminGoodwillCreditAuditPage.jsx
 * =================================
 * Super-admin / org-admin / event-manager audit surface for the
 * goodwill-credit (system_credit) ledger.  Shows totals, per-feature
 * breakdown + paginated detail rows joined with the original failed
 * consume row.
 */
import { useEffect, useMemo, useState } from "react";
import DashboardLayout from "../../components/DashboardLayout";
import {
  getGoodwillSummary, getGoodwillList, goodwillCsvUrl, formatApiError,
} from "../../lib/goodwillApi";
import {
  Sparkles, Wallet, ShieldCheck, Download, Filter, RefreshCw, AlertTriangle,
  Calendar,
} from "lucide-react";
import { toast } from "sonner";

const WINDOWS = [
  { value: 7, label: "Last 7 days" },
  { value: 30, label: "Last 30 days" },
  { value: 90, label: "Last 90 days" },
  { value: 365, label: "Last year" },
];

export default function AdminGoodwillCreditAuditPage() {
  const [days, setDays] = useState(30);
  const [summary, setSummary] = useState(null);
  const [list, setList] = useState(null);
  const [filterFeature, setFilterFeature] = useState("");
  const [filterUserId, setFilterUserId] = useState("");
  const [loading, setLoading] = useState(true);

  const reload = async () => {
    setLoading(true);
    try {
      const [s, l] = await Promise.all([
        getGoodwillSummary(days),
        getGoodwillList({
          days,
          featureKey: filterFeature || null,
          userId: filterUserId || null,
        }),
      ]);
      setSummary(s);
      setList(l);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [days]);

  const apiBase = process.env.REACT_APP_BACKEND_URL || "";
  const csvHref = `${apiBase}/api${goodwillCsvUrl(days)}`;

  const totals = summary?.totals || {};
  const byFeature = summary?.by_feature || [];
  const rows = list?.rows || [];

  return (
    <DashboardLayout title="Goodwill Credit Audit">
      <div
        data-testid="goodwill-audit-page"
        className="space-y-6 pb-12"
      >
        <Hero days={days} onChange={setDays} csvHref={csvHref} />

        {loading || !summary ? (
          <div className="grid h-72 place-items-center text-sm text-slate-500">Loading audit…</div>
        ) : (
          <>
            <TotalsRow totals={totals} />

            <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
              <main className="lg:col-span-8 space-y-6">
                <DetailFilters
                  feature={filterFeature}
                  setFeature={setFilterFeature}
                  userId={filterUserId}
                  setUserId={setFilterUserId}
                  onApply={reload}
                />
                <DetailTable rows={rows} />
              </main>

              <aside className="lg:col-span-4 space-y-6">
                <ByFeatureCard rows={byFeature} />
                <PolicyCard />
              </aside>
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}

// =============================================================================
// Hero
// =============================================================================
function Hero({ days, onChange, csvHref }) {
  return (
    <section
      data-testid="goodwill-audit-hero"
      className="relative overflow-hidden rounded-3xl border border-[#0F4C81] bg-gradient-to-br from-[#0A192F] via-[#0F4C81] to-[#0A192F] p-6 md:p-7 shadow-2xl text-white"
    >
      <div className="absolute -top-32 -right-24 h-72 w-72 rounded-full bg-[#D4AF37]/15 blur-3xl" />
      <div className="relative flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
        <div>
          <p className="text-[10px] uppercase tracking-[0.32em] text-[#D4AF37] font-bold flex items-center gap-2">
            <ShieldCheck size={12} /> Diploks · Audit
          </p>
          <h1 className="mt-1 text-3xl md:text-4xl font-bold leading-tight">
            Goodwill Credit Audit
          </h1>
          <p className="mt-2 max-w-xl text-sm text-blue-100">
            Every <em>system_credit</em> issued when a premium feature failed.
            Gross revenue stays whole — these credits are tracked separately.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div
            data-testid="goodwill-audit-window"
            className="flex items-center rounded-full bg-white/10 p-1"
          >
            {WINDOWS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => onChange(opt.value)}
                data-testid={`goodwill-audit-window-${opt.value}`}
                className={`rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-[0.18em] transition ${
                  days === opt.value
                    ? "bg-[#D4AF37] text-[#0A192F]"
                    : "text-white/90 hover:text-white"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <a
            data-testid="goodwill-audit-csv"
            href={csvHref}
            className="inline-flex items-center gap-1 rounded-lg border border-[#D4AF37]/40 bg-white/10 px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.18em] text-white hover:bg-white/20"
          >
            <Download size={11} /> Export CSV
          </a>
        </div>
      </div>
    </section>
  );
}

// =============================================================================
// KPI row + by-feature card + filter strip + table
// =============================================================================
function TotalsRow({ totals }) {
  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
      <Kpi
        testid="goodwill-kpi-gross"
        icon={Wallet}
        label="Gross consumed"
        value={`${(totals.gross_consumed_diploks || 0).toLocaleString()} Dpk`}
      />
      <Kpi
        testid="goodwill-kpi-credit"
        icon={Sparkles}
        label="System credit"
        value={`${(totals.system_credit_diploks || 0).toLocaleString()} Dpk`}
      />
      <Kpi
        testid="goodwill-kpi-net"
        icon={ShieldCheck}
        label="Net revenue"
        value={`${(totals.net_revenue_diploks || 0).toLocaleString()} Dpk`}
      />
      <Kpi
        testid="goodwill-kpi-rate"
        icon={AlertTriangle}
        label="Goodwill rate"
        value={`${(totals.goodwill_rate_pct ?? 0).toFixed(2)} %`}
      />
    </div>
  );
}

function Kpi({ icon: Icon, label, value, testid }) {
  return (
    <div data-testid={testid} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold">
        <Icon size={12} className="text-[#0F4C81]" /> {label}
      </div>
      <div className="mt-1 text-2xl font-bold text-[#0A192F] tabular-nums">{value}</div>
    </div>
  );
}

function ByFeatureCard({ rows }) {
  return (
    <section
      data-testid="goodwill-by-feature"
      className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"
    >
      <h3 className="text-[11px] uppercase tracking-[0.22em] text-[#0A192F] font-bold flex items-center gap-1">
        <Sparkles size={11} className="text-[#D4AF37]" /> Goodwill by feature
      </h3>
      {rows.length === 0 ? (
        <p className="mt-3 text-xs text-slate-500">No goodwill credits in this window.</p>
      ) : (
        <ol className="mt-3 space-y-1.5">
          {rows.map((r) => (
            <li
              key={r.feature_key}
              data-testid={`goodwill-feature-${r.feature_key}`}
              className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-1.5 text-xs"
            >
              <span className="font-semibold text-[#0A192F]">
                {r.feature_key}
              </span>
              <span className="text-right">
                <span className="font-bold text-[#0F4C81] tabular-nums">
                  {r.system_credit_diploks} Dpk
                </span>{" "}
                <span className="text-[10px] text-slate-500">
                  / {r.goodwill_rate_pct}%
                </span>
              </span>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}

function DetailFilters({ feature, setFeature, userId, setUserId, onApply }) {
  return (
    <div
      data-testid="goodwill-filter-strip"
      className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm flex flex-wrap items-center gap-2"
    >
      <Filter size={13} className="text-slate-400" />
      <input
        data-testid="goodwill-filter-feature"
        value={feature}
        onChange={(e) => setFeature(e.target.value)}
        placeholder="Feature key (eg. live_ai_in_class_notes)"
        className="flex-1 min-w-[160px] rounded-md border border-slate-300 px-2 py-1.5 text-xs"
      />
      <input
        data-testid="goodwill-filter-user"
        value={userId}
        onChange={(e) => setUserId(e.target.value)}
        placeholder="User ID"
        className="flex-1 min-w-[160px] rounded-md border border-slate-300 px-2 py-1.5 text-xs"
      />
      <button
        type="button"
        data-testid="goodwill-filter-apply"
        onClick={onApply}
        className="inline-flex items-center gap-1 rounded-md bg-[#0A192F] px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.18em] text-white hover:bg-[#0F4C81]"
      ><RefreshCw size={11} /> Apply</button>
    </div>
  );
}

function DetailTable({ rows }) {
  return (
    <section
      data-testid="goodwill-detail-table"
      className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm overflow-x-auto"
    >
      <h3 className="px-2 py-1 text-[11px] uppercase tracking-[0.22em] text-[#0A192F] font-bold flex items-center gap-1">
        <Calendar size={11} className="text-[#0F4C81]" /> Detail · {rows.length} rows
      </h3>
      {rows.length === 0 ? (
        <p className="px-2 py-6 text-xs text-slate-500">
          No goodwill credits match the current filter.
        </p>
      ) : (
        <table className="min-w-full text-[11px] mt-2">
          <thead className="bg-slate-50">
            <tr className="text-left text-[9px] uppercase tracking-[0.2em] text-slate-500">
              <th className="px-2 py-2">When</th>
              <th className="px-2 py-2">User</th>
              <th className="px-2 py-2">Feature</th>
              <th className="px-2 py-2 text-right">Credit</th>
              <th className="px-2 py-2">Reason</th>
              <th className="px-2 py-2">Original consume</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr
                key={r.credit_id}
                data-testid={`goodwill-row-${r.credit_id}`}
                className="border-t border-slate-100"
              >
                <td className="px-2 py-2 whitespace-nowrap font-mono text-[10px] text-slate-600">
                  {(r.credit_at || "").slice(0, 16).replace("T", " ")}
                </td>
                <td className="px-2 py-2">
                  <div className="font-semibold text-[#0A192F]">{r.user_email || r.user_id}</div>
                  <div className="text-[9px] text-slate-400">{r.wallet_scope}</div>
                </td>
                <td className="px-2 py-2 font-semibold text-[#0A192F]">{r.feature_key}</td>
                <td className="px-2 py-2 text-right font-bold text-[#D4AF37] tabular-nums">
                  +{r.credit_amount}
                </td>
                <td className="px-2 py-2 text-slate-600">{r.credit_reason || "—"}</td>
                <td className="px-2 py-2">
                  <div className="font-mono text-[9px] text-slate-500">{r.source_consume_id || "—"}</div>
                  <div className="text-[10px] text-slate-500">
                    cost {r.source_consume_cost ?? "—"} · {(r.source_consume_at || "").slice(0, 16).replace("T", " ")}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

function PolicyCard() {
  return (
    <section className="rounded-2xl border border-slate-200 bg-slate-50/70 p-4">
      <p className="text-[11px] text-slate-600 leading-relaxed">
        Goodwill credits restore the user&apos;s wallet without reversing the original
        consume row, so gross revenue accounting stays whole.{" "}
        <span className="font-semibold text-[#0A192F]">Net revenue</span> = gross − goodwill.
        Credits expire 30 days after issue.
      </p>
    </section>
  );
}
